import React, { useState } from 'react';
import { ChevronDown, Phone, Mail, HelpCircle, MessageCircle, Clock, MapPin } from 'lucide-react';

const Faq = () => {
  const [openItems, setOpenItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const toggleItem = (category, index) => {
    const key = `${category}-${index}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const faqData = {
    booking: {
      title: 'Booking & Scheduling',
      icon: '📅',
      color: 'blue',
      questions: [
        {
          question: 'How do I book a bike service?',
          answer: 'Booking is simple! Just visit our website or app, select your bike brand and model, choose the service type, pick a convenient date and time slot, and confirm your booking. You can also call us at +91 9112412191 for assistance. We offer doorstep pickup service across Pune.'
        },
        {
          question: 'Can I schedule a service for the same day?',
          answer: 'Yes, we offer same-day service bookings subject to slot availability. For urgent services, please call our support team at +91 9112412191. We recommend booking at least 24 hours in advance to secure your preferred time slot.'
        },
        {
          question: 'How can I track my service status?',
          answer: 'Once your booking is confirmed, you can track your service status in real-time through our app or website. You\'ll receive SMS and WhatsApp updates at every stage - pickup, diagnosis, work in progress, quality check, and delivery. You can also call our support for instant updates.'
        },
        {
          question: 'Can I reschedule my service appointment?',
          answer: 'Yes, you can reschedule your appointment free of charge if done at least 6 hours before the scheduled pickup time. To reschedule, visit your booking details in the app/website or call us at +91 9112412191. Last-minute changes may be subject to availability.'
        },
        {
          question: 'What areas do you cover in Pune?',
          answer: 'We provide free doorstep pickup and delivery across all major areas in Pune including Kothrud, Baner, Hinjewadi, Kharadi, Viman Nagar, Hadapsar, Wakad, Aundh, Shivajinagar, Koregaon Park, Pimpri, and Chinchwad. For areas outside these zones, please contact us for availability.'
        }
      ]
    },
    payment: {
      title: 'Payment & Pricing',
      icon: '💳',
      color: 'green',
      questions: [
        {
          question: 'What payment methods do you accept?',
          answer: 'We accept multiple payment methods including UPI (Google Pay, PhonePe, Paytm), credit/debit cards, net banking, and cash on delivery. You can pay after the service is completed and you\'re satisfied with the work. All online transactions are secured with SSL encryption.'
        },
        {
          question: 'Do I need to pay in advance?',
          answer: 'No advance payment is required for most services. You only pay after your bike is serviced and delivered back to you. For high-value repairs or part replacements, we may request a partial advance payment. Final billing is transparent with detailed breakup of parts and labor costs.'
        },
        {
          question: 'Are there any hidden charges?',
          answer: 'Absolutely not! We believe in 100% transparent pricing. The price you see during booking is what you pay. Any additional work or part replacements will only be done after your explicit approval. You\'ll receive a detailed invoice showing all charges clearly.'
        },
        {
          question: 'Do you provide service estimates before starting work?',
          answer: 'Yes, after our technician inspects your bike, you\'ll receive a detailed service estimate including parts cost, labor charges, and any additional recommendations. Work begins only after your approval. You have the right to decline any recommended service.'
        },
        {
          question: 'What is your cancellation policy?',
          answer: 'You can cancel your booking free of charge up to 6 hours before the scheduled pickup time. For cancellations made less than 6 hours before pickup, a nominal cancellation fee of ₹100 may apply. If we\'ve already picked up your bike, you can still cancel but may be charged for pickup/delivery.'
        }
      ]
    },
    service: {
      title: 'Service & Repairs',
      icon: '🔧',
      color: 'orange',
      questions: [
        {
          question: 'What types of services do you offer?',
          answer: 'We offer comprehensive bike services including General Service (oil change, filter replacement, tune-up), Repairs (engine, electrical, mechanical issues), Spare Parts replacement, Washing & Detailing, Custom modifications, and Annual Maintenance Contracts. We service all major brands - Hero, Honda, TVS, Bajaj, Royal Enfield, KTM, and more.'
        },
        {
          question: 'How long does a typical service take?',
          answer: 'General servicing typically takes 4-6 hours. Complex repairs may take 1-2 days depending on parts availability and work complexity. We provide accurate time estimates after initial inspection. For urgent services, we offer express service at a nominal additional charge.'
        },
        {
          question: 'Do you use genuine spare parts?',
          answer: 'Yes, we use only 100% genuine OEM (Original Equipment Manufacturer) parts sourced directly from authorized dealers. All parts come with manufacturer warranty. We also offer OES (Original Equipment Supplier) alternatives if you prefer cost-effective options. You choose what goes into your bike.'
        },
        {
          question: 'Do you provide warranty on services?',
          answer: 'Yes! All our services come with a 6-month/3000 km warranty (whichever comes first). Spare parts carry manufacturer warranty ranging from 6 months to 2 years. If you face any issues related to our service, we\'ll fix it free of charge during the warranty period.'
        },
        {
          question: 'Are your mechanics certified?',
          answer: 'Absolutely! All our mechanics are trained and certified professionals with years of experience. They undergo regular training programs on latest bike technologies and repair techniques. Our service centers are equipped with modern diagnostic tools and equipment.'
        },
        {
          question: 'Can you handle emergency breakdowns?',
          answer: 'Yes, we offer emergency breakdown assistance. Call our helpline at +91 9112412191 and we\'ll arrange immediate pickup. We prioritize emergency cases and aim to get your bike back on the road as quickly as possible.'
        }
      ]
    },
    general: {
      title: 'General Information',
      icon: 'ℹ️',
      color: 'purple',
      questions: [
        {
          question: 'What documents do I need for service?',
          answer: 'You need to provide your bike\'s Registration Certificate (RC) copy and a valid ID proof (Aadhaar, PAN, Driving License). These are required for documentation and insurance purposes. Original RC should be kept with you.'
        },
        {
          question: 'Do you offer doorstep pickup and delivery?',
          answer: 'Yes! Free doorstep pickup and delivery is included in all our services across Pune. Our rider will come to your location at the scheduled time, pick up your bike, and deliver it back after servicing. You don\'t need to visit the service center at all.'
        },
        {
          question: 'Can I wait while my bike is being serviced?',
          answer: 'While we primarily focus on doorstep service, you can visit our service centers if you prefer. However, we don\'t have customer waiting areas at most centers as we encourage the convenience of doorstep service. You can track service progress online in real-time.'
        },
        {
          question: 'What if I\'m not satisfied with the service?',
          answer: 'Customer satisfaction is our top priority. If you\'re not happy with our service, contact us immediately at +91 9112412191. We\'ll re-inspect and rectify any issues free of charge. Your feedback helps us improve our services continuously.'
        },
        {
          question: 'Do you service electric bikes/scooters?',
          answer: 'Yes, we service electric two-wheelers including brands like Ather, Ola Electric, TVS iQube, Bajaj Chetak, and others. Our technicians are trained in EV technology and use specialized equipment for battery diagnostics and maintenance.'
        },
        {
          question: 'How do I contact customer support?',
          answer: 'You can reach our customer support team via Phone: +91 9112412191 (9 AM - 8 PM, Mon-Sun), Email: okbikerentals@gmail.com, WhatsApp: +91 9112412191, or through the Live Chat feature on our website/app. We respond to all queries within 2 hours.'
        }
      ]
    }
  };

  // Filter FAQs based on search
  const getFilteredData = () => {
    if (!searchTerm) return faqData;

    const filtered = {};
    Object.entries(faqData).forEach(([key, category]) => {
      const filteredQuestions = category.questions.filter(
        q =>
          q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.answer.toLowerCase().includes(searchTerm.toLowerCase())
      );

      if (filteredQuestions.length > 0) {
        filtered[key] = {
          ...category,
          questions: filteredQuestions
        };
      }
    });

    return filtered;
  };

  const filteredData = getFilteredData();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-blue-600 text-white py-12 sm:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <HelpCircle className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg sm:text-xl text-blue-100 mb-8">
            Find quick answers about OkMechanic bike servicing
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for questions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-6 py-4 rounded-xl text-gray-900 text-lg focus:outline-none focus:ring-4 focus:ring-blue-300"
              />
              <HelpCircle className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-6 h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="max-w-7xl mx-auto px-4 -mt-8 mb-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-md p-4 text-center border-t-4 border-blue-600">
            <div className="text-2xl font-bold text-blue-600 mb-1">24/7</div>
            <div className="text-xs text-gray-600">Support Available</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 text-center border-t-4 border-green-600">
            <div className="text-2xl font-bold text-green-600 mb-1">5000+</div>
            <div className="text-xs text-gray-600">Happy Customers</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 text-center border-t-4 border-orange-600">
            <div className="text-2xl font-bold text-orange-600 mb-1">100%</div>
            <div className="text-xs text-gray-600">Genuine Parts</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-4 text-center border-t-4 border-purple-600">
            <div className="text-2xl font-bold text-purple-600 mb-1">6 Months</div>
            <div className="text-xs text-gray-600">Service Warranty</div>
          </div>
        </div>
      </div>

      {/* FAQ Categories */}
      <div className="max-w-5xl mx-auto px-4 pb-12">
        {Object.keys(filteredData).length === 0 ? (
          <div className="bg-white rounded-2xl shadow-md p-12 text-center">
            <div className="text-5xl mb-4">🔍</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No results found</h3>
            <p className="text-gray-600">Try searching with different keywords</p>
          </div>
        ) : (
          Object.entries(filteredData).map(([key, category]) => (
            <div key={key} className="mb-10">
              {/* Category Header */}
              <div className={`bg-${category.color}-600 text-white rounded-2xl p-6 mb-6 shadow-lg`}>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center text-3xl">
                    {category.icon}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">{category.title}</h2>
                    <p className="text-sm text-white/80 mt-1">
                      {category.questions.length} question{category.questions.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              </div>

              {/* Questions */}
              <div className="space-y-3">
                {category.questions.map((item, index) => {
                  const itemKey = `${key}-${index}`;
                  const isOpen = openItems[itemKey];

                  return (
                    <div
                      key={index}
                      className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-all"
                    >
                      <button
                        onClick={() => toggleItem(key, index)}
                        className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-gray-900 pr-4 text-base sm:text-lg">
                          {item.question}
                        </span>
                        <ChevronDown
                          className={`w-6 h-6 text-gray-500 flex-shrink-0 transition-transform duration-300 ${
                            isOpen ? 'transform rotate-180' : ''
                          }`}
                        />
                      </button>
                      <div
                        className={`transition-all duration-300 ease-in-out ${
                          isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="px-6 pb-5 text-gray-700 leading-relaxed border-t border-gray-100 pt-4 text-sm sm:text-base">
                          {item.answer}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Contact Support Section */}
     

      {/* Quick Links */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl shadow-md p-6 border-l-4 border-blue-600 hover:shadow-lg transition-all">
            <MapPin className="w-8 h-8 text-blue-600 mb-4" />
            <h3 className="font-bold text-gray-900 mb-2 text-lg">Visit Us</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              32, C Lane, Ragvilas Society, Koregaon Park, Pune, Maharashtra 411001
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-md p-6 border-l-4 border-green-600 hover:shadow-lg transition-all">
            <Phone className="w-8 h-8 text-green-600 mb-4" />
            <h3 className="font-bold text-gray-900 mb-2 text-lg">Call Us</h3>
            <p className="text-sm text-gray-600 mb-2">Customer Support:</p>
            <a href="tel:+919112412191" className="text-green-600 font-semibold hover:underline">
              +91 9112412191
            </a>
          </div>

        
        </div>
      </div>
    </div>
  );
};

export default Faq;
